import React, { useMemo, useRef, useState, useEffect } from "react";
import ReactQuill from "react-quill-new";
import "quill/dist/quill.snow.css";

import { useBoxContext } from "../../../../Contexts/boxData.context.jsx";
import { useMask } from "../../../../Contexts/mask.context.jsx";
import { usePlayer } from "../../../../Contexts/playerData.context.jsx";
import fbxLoadingGif from "../../../../assets/Logo/FBX-Gif.gif";

const NOTES_ENDPOINT = "https://www.fbxchange.com/ReactPHP/notes.php";
const SCRIPT_CHANGE_PRIVATE = "changePrivate";
const SCRIPT_DELETE_NOTE = "deleteNote";

const PATCH_EVENT = "FBX_NOTE_LIST_PATCH";
const DIRTY_EVENT = "FBX_NOTE_EDITOR_DIRTY";

const RichNoteEditor = ({
  value,
  onChange,
  placeholder,
  readOnly,
  noteData = {},
  letsSaveNote = async () => {},
  letsDeleteNote = null,

  // Parent may pass "100%" or a px number/string
  height = "100%",
}) => {
  const { setNoteBoxID, setNoteBoxOpen } = useBoxContext();
  const { mask, unmask } = useMask();
  const { activePlayer } = usePlayer();
  const quillRef = useRef(null);

  const [title, setTitle] = useState(noteData?.SourceData || "");
  const [dateOfContact, setDateOfContact] = useState(noteData?.DateOfContact || "");
  const [hydrated, setHydrated] = useState(false);

  const [meName, setMeName] = useState("");
  const [privateFlag, setPrivateFlag] = useState(false);

  const [showDeleteBox, setShowDeleteBox] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const [isTogglingPrivate, setIsTogglingPrivate] = useState(false);
  const [editorDirty, setEditorDirty] = useState(false);
  const [isAskingAi, setIsAskingAi] = useState(false);
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiError, setAiError] = useState("");
  const [showAiBox, setShowAiBox] = useState(false);
  const [showAskAiConfirm, setShowAskAiConfirm] = useState(false);
  const [aiCopyStatus, setAiCopyStatus] = useState("");

  const originalRef = useRef({
    title: noteData?.SourceData || "",
    date: noteData?.DateOfContact || "",
    html: value || "",
  });

  const postLoadSnapDoneRef = useRef(false);

  const norm = (s) => (s ?? "").replace(/\s+/g, " ").trim();
  const isMeaningful = (s) => norm(s).length > 0;
  const toTitleCase = (s) =>
    norm(s)
      .toLowerCase()
      .replace(/\b[a-z]/g, (char) => char.toUpperCase());
  const stripAccents = (s) => norm(s).normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const normalizeHtml = (html) => {
    let s = (html ?? "").toString().trim();
    if (s === "<p><br></p>" || s === "<p></p>" || s === "<div><br></div>") return "";
    s = s.replace(/&nbsp;/g, " ");
    s = s.replace(/\s+/g, " ").trim();
    return s;
  };

  const getExpectedHtmlForNote = (note) => {
    const comm = note?.Communication;
    if (comm && typeof comm === "object") return comm?.html ?? comm?.text ?? "";
    if (typeof comm === "string") return comm;
    if (typeof note?.html === "string") return note.html;
    if (typeof note?.HTML === "string") return note.HTML;
    return "";
  };

  const decodeJwtPayload = (token) => {
    if (!token) return null;
    try {
      return JSON.parse(
        decodeURIComponent(
          atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join("")
        )
      );
    } catch (e) {
      console.error("Error decoding JWT:", e);
      return null;
    }
  };

  const isPrivateNote = (note) => {
    const raw = note?.private1 ?? note?.private ?? note?.isPrivate ?? note?.Private ?? note?.PRIVATE;
    if (raw == null) return false;
    if (typeof raw === "boolean") return raw;
    const s = String(raw).trim().toLowerCase();
    return s === "1" || s === "y" || s === "yes" || s === "true";
  };

  const getNoteId = () => noteData?.CommId ?? noteData?.id ?? null;
  const getFBXID = () => noteData?.DRNUM ?? noteData?.FBXID ?? null;
  const getTeam = () => noteData?.VisibleTo ?? "";

  const playerForAi = useMemo(() => {
    const getPlayerField = (...keys) => {
      const sources = [activePlayer || {}, noteData || {}];
      for (const source of sources) {
        for (const key of keys) {
          const value = source?.[key];
          if (value !== undefined && value !== null && String(value).trim() !== "") {
            return String(value).trim();
          }
        }
      }
      return "";
    };
    const getProTeam = (league, teamKeys, statusKeys, dateKeys) => {
      const team = getPlayerField(...teamKeys);
      if (!team) return null;

      return {
        league,
        team,
        status: getPlayerField(...statusKeys),
        date: getPlayerField(...dateKeys),
      };
    };

    const proTeams = [
      getProTeam("CFL", ["CFLTEAM", "cflTeam", "CFLTeam"], ["CFLSTATUS", "cflStatus", "CFLStatus"], ["CFLDATE", "cflDate", "CFLDate"]),
      getProTeam("NFL", ["NFLTEAM", "nflTeam", "NFLTeam"], ["NFLSTATUS", "nflStatus", "NFLStatus"], ["NFLDATE", "nflDate", "NFLDate"]),
      getProTeam("UFL24", ["UFL24TEAM", "ufl24Team", "UFL24Team"], ["UFL24STATUS", "ufl24Status", "UFL24Status"], ["UFL24DATE", "ufl24Date", "UFL24Date"]),
    ].filter(Boolean);

	    return {
	      first: getPlayerField("first", "First", "FIRST", "firstName", "FirstName"),
	      last: getPlayerField("last", "Last", "LAST", "lastName", "LastName"),
	      school: getPlayerField("longSchool", "LONGSCHOOL", "schoolLong", "SchoolLong", "school", "School", "COLLEGE", "college"),
	      schoolCode: getPlayerField("school", "School", "SCHOOL"),
	      position: getPlayerField("colPos1", "COL_POS1", "collegePosition", "CollegePosition", "colPos", "position", "Position", "genPos"),
	      highSchool: getPlayerField("highSchool", "HighSchool", "HIGH_SCHOOL", "HS", "LAST_SCHOOL", "lastSchool"),
	      hometown: getPlayerField("homeTown", "hometown", "HomeTown", "HOMETOWN"),
	      homeState: getPlayerField("homeState", "HOMESTATE", "state", "STATE"),
	      classYear: getPlayerField("optionYr", "OPTION_YR", "classYear", "ClassYear", "eligibility", "Eligibility"),
	      height: getPlayerField("height", "HEIGHT", "Height"),
	      weight: getPlayerField("weight", "WEIGHT", "Weight"),
	      draftYear: getPlayerField("nflDraftYear", "DRAFT_YEAR", "draftYear", "DraftYear", "NFLDraftYear"),
	      cflDraftYear: getPlayerField("cflDraftYear", "CFL_DRAFT_YEAR", "cflDraftYr", "CFLDraftYear"),
	      ufl24DraftYear: getPlayerField("ufl24DraftYear", "UFL24DY", "UFL24_DRAFT_YEAR", "UFL24DraftYear"),
	      proTeams,
	    };
	  }, [activePlayer, noteData]);

  const playerNameForAi = [playerForAi.first, playerForAi.last].filter(Boolean).join(" ").trim();
  const playerSearchNamesForAi = Array.from(
    new Set(
      [
        playerNameForAi,
        playerNameForAi.toLowerCase(),
        toTitleCase(playerNameForAi),
        [playerForAi.last, playerForAi.first].filter(Boolean).join(" ").trim(),
        [playerForAi.last, playerForAi.first].filter(Boolean).join(", ").trim(),
      ]
        .flatMap((name) => [name, stripAccents(name)])
        .map(norm)
        .filter(Boolean)
    )
  );
  const playerSearchQueriesForAi = Array.from(
    new Set(
      playerSearchNamesForAi
        .flatMap((name) => [
          name,
          playerForAi.school ? `"${name}" "${playerForAi.school}" football roster` : "",
          playerForAi.schoolCode ? `"${name}" "${playerForAi.schoolCode}" football roster` : "",
          ...playerForAi.proTeams.map((team) => `"${name}" "${team.team}" ${team.league}`),
        ])
        .map(norm)
        .filter(Boolean)
    )
  );
  const playerAiDisplay = [
    playerNameForAi || "Player",
    playerForAi.position ? `[${playerForAi.position}]` : "",
    playerForAi.school || "",
  ].filter(Boolean).join(" ");

  const emitDirtyState = (dirty) => {
    try {
      window.dispatchEvent(new CustomEvent(DIRTY_EVENT, { detail: { dirty: !!dirty } }));
    } catch {}
  };


  const emitPatchUpdate = (id, patch) => {
    if (!id) return;
    window.dispatchEvent(new CustomEvent(PATCH_EVENT, { detail: { action: "update", id, patch } }));
  };

  const emitPatchRemove = (id) => {
    if (!id) return;
    window.dispatchEvent(new CustomEvent(PATCH_EVENT, { detail: { action: "remove", id } }));
  };

  const pruneLocalNoteTokenIfMatches = (FBXID, deletedId) => {
    const TOKEN_KEY = "noteToken";
    if (!FBXID || deletedId == null) return;

    try {
      const raw = localStorage.getItem(TOKEN_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      const list = Array.isArray(parsed) ? parsed : [parsed];

      const next = list
        .map((r) => {
          if (String(r?.FBXID) !== String(FBXID)) return r;
          if (String(r?.NoteID) !== String(deletedId)) return r;
          return { ...r, NoteID: null };
        })
        .filter(Boolean);

      localStorage.setItem(TOKEN_KEY, JSON.stringify(next));
    } catch (e) {
      console.warn("Failed to prune noteToken after delete:", e);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("user2026");
    const payload = decodeJwtPayload(token);
    setMeName(String(payload?.displayName || "").trim());
  }, []);

  useEffect(() => {
    setPrivateFlag(isPrivateNote(noteData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteData?.CommId, noteData?.id, noteData?.private1]);

  const expectedHtml = useMemo(() => {
    return normalizeHtml(getExpectedHtmlForNote(noteData));
  }, [noteData?.CommId, noteData?.id, noteData?.synchdate]);

  useEffect(() => {
    const nextTitle = noteData?.SourceData || "";
    const nextDate = noteData?.DateOfContact || "";

    setTitle(nextTitle);
    setDateOfContact(nextDate);

    originalRef.current = { title: nextTitle, date: nextDate, html: expectedHtml || "" };
    setHydrated(false);
    setEditorDirty(false);
    postLoadSnapDoneRef.current = false;

    setShowDeleteBox(false);
    setDeleteError("");

    try {
      unmask();
    } catch {}
    emitDirtyState(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteData?.CommId, noteData?.id, expectedHtml]);

  useEffect(() => {
    if (postLoadSnapDoneRef.current) return;

    const currentHtml = normalizeHtml(value);
    const wantHtml = normalizeHtml(expectedHtml);

    const hasAnyMeaningful =
      isMeaningful(title) ||
      isMeaningful(dateOfContact) ||
      isMeaningful(currentHtml) ||
      isMeaningful(wantHtml);

    const matches = currentHtml === wantHtml;
    const bothEmpty = currentHtml === "" && wantHtml === "";

    if (hasAnyMeaningful && (matches || bothEmpty)) {
      originalRef.current = { title, date: dateOfContact, html: currentHtml };
      postLoadSnapDoneRef.current = true;
      setHydrated(true);
      setEditorDirty(false);
      try {
        unmask();
      } catch {}
      emitDirtyState(false);
    }
  }, [title, dateOfContact, value, expectedHtml]);

  const isDirty = useMemo(() => {
    if (!hydrated) return false;
    const { title: oTitle, date: oDate } = originalRef.current;

    return (
      norm(title) !== norm(oTitle) ||
      norm(dateOfContact) !== norm(oDate) ||
      editorDirty
    );
  }, [hydrated, title, dateOfContact, editorDirty]);

  const prevDirtyRef = useRef(false);
  useEffect(() => {
    const dirtyNow = hydrated && isDirty;

    if (dirtyNow !== prevDirtyRef.current) {
      prevDirtyRef.current = dirtyNow;
      try {
        if (dirtyNow) mask();
        else unmask();
      } catch {}
      emitDirtyState(dirtyNow);
    }
  }, [hydrated, isDirty, mask, unmask]);

  useEffect(() => {
    return () => emitDirtyState(false);
  }, []);

  const whosNote = useMemo(() => {
    const by = String(noteData?.ByName || "").trim();
    if (!by || !meName) return "NOTME";
    return by === meName ? "ME" : "NOTME";
  }, [noteData?.ByName, meName]);

  const canModifyCurrent = whosNote === "ME" && !!getNoteId();

  const privateButtonLabel = privateFlag ? "Set Public" : "Set Private";
  const privateButtonClass = privateFlag
    ? "col-11 btn btn-purple btn-sm"
    : "col-11 btn btn-outline-purple btn-sm";

  const handleImage = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = (input.files && input.files[0]) || null;
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        const quill = quillRef.current?.getEditor();
        const range = quill?.getSelection(true);
        if (quill && range) {
          quill.insertEmbed(range.index, "image", reader.result, "user");
          quill.setSelection(range.index + 1, 0, "user");
        }
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const modules = React.useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
          [{ color: [] }, { background: [] }],
          [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
          [{ align: [] }],
          ["link", "image"],
          ["clean"],
        ],
        handlers: { image: handleImage },
      },
      clipboard: { matchVisual: false },
    }),
    []
  );

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "code-block",
    "color",
    "background",
    "list",
    "indent",
    "align",
    "link",
    "image",
  ];

  const handleQuillChange = (html, delta, source) => {
    try {
      onChange(html);
    } catch {}

    if (!readOnly && hydrated && source === "user") {
      setEditorDirty(true);
    }
  };

  const readAiAnswer = (data) => {
    if (typeof data === "string") return data;
    if (!data || typeof data !== "object") return "";
    return data.error || data.answer || data.response || data.message || data.text || data.content || data.result || "";
  };

  const getFriendlyAiError = (error) => {
    const message = String(error?.message || error || "").trim();
    const lower = message.toLowerCase();
    const timedOut =
      lower.includes("timeout") ||
      lower.includes("timed out") ||
      lower.includes("failed to fetch") ||
      lower.includes("networkerror") ||
      lower.includes("502") ||
      lower.includes("504");

	    if (timedOut) {
	      return "Please try again in a minute, my server is currently busy.";
	    }

	    return message || "Unable to build a note with AI at this time. We are in Beta Testing.";
  };

  const formatSources = (sources) => {
    if (!Array.isArray(sources) || sources.length === 0) return "";
    const unique = [];
    sources.forEach((source) => {
      const url = String(source?.url || "").trim();
      if (!url || unique.some((item) => item.url === url)) return;
      unique.push({
        title: String(source?.title || url).trim(),
        url,
      });
    });
    if (unique.length === 0) return "";
    return [
      "Sources:",
      ...unique.slice(0, 6).map((source, index) => `${index + 1}. ${source.title} - ${source.url}`),
    ].join("\n");
  };

	  const buildPlayerResearchPrompt = () => {
	    const name = playerNameForAi || "Unknown player";
	    const school = playerForAi.school || "Unknown school";
	    const position = playerForAi.position || "Unknown position";
	    const classYear = playerForAi.classYear || "Unknown";
	    const hometown = [playerForAi.hometown, playerForAi.homeState].filter(Boolean).join(", ") || "Unknown";
	    const highSchool = playerForAi.highSchool || "Unknown";
	    const heightValue = playerForAi.height || "Unknown";
	    const weightValue = playerForAi.weight || "Unknown";
	    const existingNotes = normalizeHtml(value || getExpectedHtmlForNote(noteData) || noteData?.SourceData || "").replace(/<[^>]*>/g, " ").trim() || "None provided";
	    const proTeamLines = playerForAi.proTeams.map((team) =>
	      `Pro team: ${team.league} - Team: ${team.team}${team.status ? ` - Team Status: ${team.status}` : ""}${team.date ? ` - Date: ${team.date}` : ""}`
	    );

	    return [
	      "Research and update the football player profile using the FBX player information provided below.",
	      "",
	      "Important:",
	      "Make this a fast, focused web check. Prefer the newest reliable sources, but stop after a concise pass through official roster/profile pages, stats pages, and recent football news or transaction sources.",
	      "Use 3 to 5 reliable sources when available. If the current status is unclear after that, say so instead of continuing to search.",
	      "",
	      "FBX Player Input:",
	      `- Name: ${name}`,
	      `- Position: ${position}`,
	      `- College/School: ${school}`,
	      `- Class/Eligibility: ${classYear}`,
	      `- Hometown: ${hometown}`,
	      `- High School: ${highSchool}`,
	      `- Height: ${heightValue}`,
	      `- Weight: ${weightValue}`,
	      `- Existing FBX Notes: ${existingNotes}`,
	      playerForAi.schoolCode && playerForAi.schoolCode !== school ? `- School code: ${playerForAi.schoolCode}` : "",
	      playerForAi.draftYear ? `- NFL draft year: ${playerForAi.draftYear}` : "",
	      playerForAi.cflDraftYear ? `- CFL draft year: ${playerForAi.cflDraftYear}` : "",
	      playerForAi.ufl24DraftYear ? `- UFL24 draft year: ${playerForAi.ufl24DraftYear}` : "",
	      ...proTeamLines.map((line) => `- ${line}`),
	      "",
	      "Search requirements:",
	      "1. Start with the player name, position, and school from the FBX input.",
	      "2. Check official roster/profile data plus one or two broader sports/news/profile sources.",
	      "3. Use targeted searches only when needed: transfer, draft, signed, released, injured, stats, hometown, or high school.",
	      "4. If the player changed schools, entered a draft, signed professionally, changed positions, or changed eligibility/class, update the profile using the newest reliable source.",
	      "5. If an older source conflicts with newer sources, treat the older source as possibly outdated unless it clearly has newer publication/update information.",
	      "",
	      "Return the result in this structure:",
	      "",
	      "Current Player Profile:",
	      "- Name:",
	      "- Position:",
	      "- Current / Most Recent Team or School:",
	      "- Previous School(s):",
	      "- Height:",
	      "- Weight:",
	      "- Class / Eligibility:",
	      "- Hometown:",
	      "- High School:",
	      "- Recruiting Background:",
	      "- Transfer History:",
	      "- College Timeline by Year:",
	      "- Career Stats Summary:",
	      "- Awards / Honors:",
	      "- Injury Notes:",
	      "- Draft / Pro Status:",
	      "- Recent News:",
	      "- Source Conflict Notes:",
	      "- Confidence Level:",
	      "",
	      "Source Conflict Rules:",
	      "- Clearly identify when FBX data appears outdated.",
	      "- Clearly identify when a source is old, incomplete, or conflicts with newer information.",
	      "- Do not overwrite FBX data unless newer reliable sources support the change.",
	      "- If information cannot be verified, mark it as \"Unverified\" instead of guessing.",
	      "",
	      "Final instruction:",
	      "Build the most current football profile possible in a concise note-friendly format.",
	      "Include source URLs at the end.",
	    ].filter(Boolean).join("\n");
	  };

  const handleAskAi = async () => {
    if (readOnly || isAskingAi || !playerNameForAi) return;

    setIsAskingAi(true);
    setAiAnswer("");
    setAiError("");
    setAiCopyStatus("");
    setShowAiBox(true);

    try {
      const endpoint =
        process.env.REACT_APP_SCOUT_AI_ENDPOINT ||
        "https://www.fbxchange.com/ReactPHP/ask-ai.php";

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: "Tell me about this player.",
          webSearch: true,
          tools: [{ type: "web_search" }],
          reasoningEffort: "low",
          verbosity: "low",
          searchContextSize: "low",
          maxOutputTokens: 1200,
          player: {
            ...playerForAi,
            collegePosition: playerForAi.position,
            searchNames: playerSearchNamesForAi,
            searchQueries: playerSearchQueriesForAi,
          },
          prompt: buildPlayerResearchPrompt(),
        }),
      });

      const text = await res.text();
      let data = text;
      try {
        data = text ? JSON.parse(text) : "";
      } catch {}

      if (!res.ok) {
        throw new Error(readAiAnswer(data) || `AI request failed (${res.status}).`);
      }
      if (data && typeof data === "object" && data.ok === false) {
        throw new Error(data.error || data.message || "AI request failed.");
      }

      const answer = readAiAnswer(data) || JSON.stringify(data, null, 2);
      const sources = data && typeof data === "object" ? formatSources(data.sources) : "";
      setAiAnswer([answer.trim(), sources].filter(Boolean).join("\n\n"));
    } catch (err) {
      console.error("Ask AI from notes failed:", err);
      setAiError(getFriendlyAiError(err));
    } finally {
      setIsAskingAi(false);
    }
  };

  const onAskAiClick = () => {
    if (readOnly || isAskingAi || !playerNameForAi) return;
    setShowAskAiConfirm(true);
  };

  const onAskAiCancel = () => {
    setShowAskAiConfirm(false);
  };

  const onAskAiProceed = () => {
    setShowAskAiConfirm(false);
    handleAskAi();
  };

  const insertTextIntoEditor = (text, mode) => {
    const cleanText = String(text || "").trim();
    const quill = quillRef.current?.getEditor();
    if (!cleanText || !quill || readOnly) return;

    if (mode === "replace") {
      quill.setText(cleanText, "user");
      quill.setSelection(quill.getLength(), 0, "user");
    } else if (mode === "merge") {
      const length = Math.max(0, quill.getLength() - 1);
      const prefix = length > 0 ? "\n\n" : "";
      quill.insertText(length, `${prefix}${cleanText}`, "user");
      quill.setSelection(quill.getLength(), 0, "user");
    } else {
      const range = quill.getSelection(true) || { index: Math.max(0, quill.getLength() - 1), length: 0 };
      quill.insertText(range.index, cleanText, "user");
      quill.setSelection(range.index + cleanText.length, 0, "user");
    }

    setEditorDirty(true);
    setShowAiBox(false);
  };

  const handleCopyAiAnswer = async () => {
    if (!aiAnswer) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(aiAnswer);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = aiAnswer;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setAiCopyStatus("Copied");
      window.setTimeout(() => setAiCopyStatus(""), 1600);
    } catch (err) {
      console.error("Copy AI note answer failed:", err);
      setAiCopyStatus("Copy failed");
    }
  };

  const onSaveClick = async () => {
    const prevComm = noteData?.Communication;
    const nextComm = prevComm && typeof prevComm === "object" ? { ...prevComm, html: value } : value;

    const updatedNote = {
      ...noteData,
      SourceData: title,
      DateOfContact: dateOfContact,
      Communication: nextComm,
      html: value,
    };

    await letsSaveNote(updatedNote);

    originalRef.current = { title, date: dateOfContact, html: normalizeHtml(value) || "" };
    setEditorDirty(false);
    try {
      unmask();
    } catch {}
    emitDirtyState(false);

    const id = getNoteId();
    emitPatchUpdate(id, {
      SourceData: title,
      DateOfContact: dateOfContact,
      private1: privateFlag ? 1 : 0,
    });
  };

  const onCancelClick = () => {
    const { title: oTitle, date: oDate, html: oHtml } = originalRef.current;

    setTitle(oTitle || "");
    setDateOfContact(oDate || "");

    try {
      onChange(oHtml || "");
    } catch {}
    try {
      unmask();
    } catch {}
    emitDirtyState(false);
  };

  const onTogglePrivateClick = () => {
    if (!canModifyCurrent) return;
    if (isTogglingPrivate) return;

    const noteID = getNoteId();
    const next = !privateFlag;

    setPrivateFlag(next);
    emitPatchUpdate(noteID, { private1: next ? 1 : 0 });

    setIsTogglingPrivate(true);

    const payload = {
      scriptName: SCRIPT_CHANGE_PRIVATE,
      noteID: Number(noteID),
      private1: next ? "1" : "0",
    };

    try {
      fetch(NOTES_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).catch((err) => console.error("changePrivate failed", err));
    } catch (err) {
      console.error("changePrivate scheduling error", err);
    } finally {
      setTimeout(() => setIsTogglingPrivate(false), 250);
    }
  };

  const onDeleteClick = () => {
    if (!canModifyCurrent) return;
    setDeleteError("");
    setShowDeleteBox(true);
  };

  const onDeleteCancel = () => {
    if (isDeleting) return;
    setShowDeleteBox(false);
    setDeleteError("");
  };

  const onDeleteConfirm = async () => {
    if (!canModifyCurrent) return;

    const noteID = getNoteId();
    const FBXID = getFBXID();
    const team = getTeam();

    if (!noteID) return;

    setIsDeleting(true);
    setDeleteError("");

    try {
      if (typeof letsDeleteNote === "function") {
        const result = await letsDeleteNote({ noteID, FBXID, team });
        if (!result?.ok) throw new Error(result?.error || "Delete failed.");
      } else {
        const res = await fetch(NOTES_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            scriptName: SCRIPT_DELETE_NOTE,
            noteID: Number(noteID),
            FBXID: FBXID ?? "",
            team: team ?? "",
          }),
        });

        const json = await res.json().catch(() => null);
        const ok = (json && json.success === true) || (json && Array.isArray(json.notes));
        if (!res.ok || !ok) {
          const msg = json?.error || json?.message || `Delete failed (HTTP ${res.status})`;
          throw new Error(msg);
        }
      }

      if (FBXID) pruneLocalNoteTokenIfMatches(FBXID, noteID);

      setShowDeleteBox(false);
      try {
        unmask();
      } catch {}

      setNoteBoxID(null);
      setNoteBoxOpen(false);

      emitPatchRemove(noteID);
    } catch (err) {
      console.error(err);
      setDeleteError(err?.message || "Delete failed.");
    } finally {
      setIsDeleting(false);
    }
  };

  const saveDisabled = !hydrated || !isDirty;

  // ✅ normalize height/minHeight so "100%" works consistently
  const normalizeSize = (h) => {
    if (h == null) return "100%";
    if (typeof h === "number") return `${h}px`;
    return String(h);
  };
  const h = normalizeSize(height);

  return (
    // ✅ minHeight: fills available space; height: 100% keeps flex layout stable
    <div className="note-editor-wrap position-relative" style={{ height: "100%", minHeight: h }}>
      {showDeleteBox && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ zIndex: 5000, background: "rgba(0,0,0,0.35)", padding: 12 }}
        >
          <div className="alert alert-danger shadow-lg m-0" style={{ width: 700, maxWidth: "95%" }}>
            <div className="d-flex justify-content-between align-items-center">
              <div className="fw-bold text-uppercase">Remove Note</div>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onDeleteCancel}
                disabled={isDeleting}
              />
            </div>

            <div className="mt-2">
              <div className="fw-bold">Are you sure you want to delete this note?</div>

              <div className="small mt-2">
                <span className="fw-bold">Date:</span> {noteData?.DateOfContact ?? "-"}
                <span className="mx-2">|</span>
                <span className="fw-bold">Title:</span> {noteData?.SourceData ?? "-"}
              </div>

              {deleteError ? <div className="alert alert-warning mt-3 mb-0 py-2">{deleteError}</div> : null}
            </div>

            <div className="d-flex gap-2 justify-content-end mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={onDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? "Removing..." : "Yes, Delete Note"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAskAiConfirm && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ zIndex: 5000, background: "rgba(0,0,0,0.35)", padding: 12 }}
        >
          <div className="bg-white border rounded shadow-lg" style={{ width: 560, maxWidth: "95%" }}>
            <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom bg-light">
              <div className="fw-bold">Ask AI</div>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onAskAiCancel}
              />
            </div>

            <div className="p-3">
              <div className="fw-bold mb-2">ASK AI can take up to a minute to do its research.</div>
              <div>Do you want to proceed or Cancel?</div>

              <div className="d-flex gap-2 justify-content-end mt-3">
                <button type="button" className="btn btn-outline-secondary" onClick={onAskAiCancel}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={onAskAiProceed}>
                  Proceed
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAiBox && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ zIndex: 4500, background: "rgba(0,0,0,0.32)", padding: 12 }}
        >
          <div className="bg-white border rounded shadow-lg" style={{ width: 760, maxWidth: "96%" }}>
            <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom bg-light">
              <div className="fw-bold">
                Ask AI: {playerAiDisplay}
              </div>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setShowAiBox(false)}
                disabled={isAskingAi}
              />
            </div>

            <div className="p-3">
              {isAskingAi ? (
                <div className="border rounded p-3 small mb-0 bg-light text-dark text-center">
                  <img
                    src={fbxLoadingGif}
                    alt="AI is working"
                    className="mb-2"
                    style={{ width: 86, height: 86, objectFit: "contain" }}
                  />
                  <div className="fw-bold fs-6">AI Working</div>
                  <div className="text-muted">
                    This can take up to 1 minute, thank you for your patience.
                  </div>
                </div>
              ) : aiError ? (
                <div className="alert alert-warning mb-0">{aiError}</div>
              ) : (
                <textarea className="form-control fs-7" rows={12} readOnly value={aiAnswer} />
              )}

              <div className="d-flex gap-2 justify-content-end mt-3">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleCopyAiAnswer} disabled={!aiAnswer}>
                  {aiCopyStatus || "Copy to Clipboard"}
                </button>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => insertTextIntoEditor(aiAnswer, "replace")} disabled={!aiAnswer}>
                  Copy to Note
                </button>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => insertTextIntoEditor(aiAnswer, "merge")} disabled={!aiAnswer}>
                  Merge to Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="note-editor-header">
        <div className="d-flex align-items-center fs-7 rounded gap-2 mb-0">
          <div className="d-flex gap-2 align-items-center flex-wrap p-2 mt-0">
            <div className="d-flex flex-column gap-1 col-14">
              <div className="text-muted small lh-1 mb-1 ps-1">Note Title</div>
              <input
                type="text"
                className="form-control fs-7"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Add a title…"
                maxLength={35}
                readOnly={!!readOnly}
              />
            </div>

            <div className="d-flex flex-column gap-1 col-9">
              <div className="text-muted small lh-1 mb-1 ps-1">Date of Contact</div>
              <input
                type="text"
                className="form-control fs-7"
                value={dateOfContact}
                onChange={(e) => setDateOfContact(e.target.value)}
                placeholder="YYYY-MM-DD"
                readOnly={!!readOnly}
              />
            </div>

            <div className="d-flex flex-column gap-1 col-6">
              <div className="text-muted small lh-1 mb-1 ps-1">By</div>
              <input type="text" className="form-control fs-7" readOnly value={noteData?.ByName || ""} />
            </div>

            <div className="d-flex flex-column gap-1 col-10">
              <div className="text-muted small lh-1 mb-1 ps-1">Last Update</div>
              <input type="text" className="form-control fs-7" readOnly value={noteData?.synchdate || ""} />
            </div>
          </div>

          <div className="ms-auto flex-grow-1 justify-content-end d-flex gap-2">
            {!isDirty && (
              <button
                className="btn btn-outline-primary btn-sm w-50"
                type="button"
                onClick={onAskAiClick}
                disabled={!!readOnly || isAskingAi || !playerNameForAi}
                title={!playerNameForAi ? "No player name found" : "Research this player with AI"}
              >
                {isAskingAi ? "Working..." : "Ask AI"}
              </button>
            )}

            {!isDirty && (
              <button
                className={privateButtonClass}
                type="button"
                onClick={onTogglePrivateClick}
                disabled={!canModifyCurrent || isTogglingPrivate}
                title={!canModifyCurrent ? "Not your note" : ""}
              >
                {privateButtonLabel}
              </button>
            )}

            {!isDirty && (
              <button
                className="btn btn-outline-danger btn-sm w-50"
                type="button"
                onClick={onDeleteClick}
                disabled={!canModifyCurrent}
                title={!canModifyCurrent ? "Not your note" : ""}
              >
                Delete
              </button>
            )}

            <button
              className={`btn btn-sm w-50 ${isDirty ? "btn-secondary" : "btn-outline-secondary"}`}
              type="button"
              onClick={onCancelClick}
              disabled={!hydrated || !isDirty}
            >
              Cancel
            </button>

            <button
              className={`btn btn-sm w-50 ${saveDisabled ? "btn-outline-success" : "btn-success"}`}
              type="button"
              onClick={onSaveClick}
              disabled={saveDisabled}
            >
              Save
            </button>
          </div>
        </div>
      </div>

      <div className="note-editor-body">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={handleQuillChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder || "Write your note…"}
          readOnly={!!readOnly}
        />
      </div>

      <style>{`
        .note-editor-wrap {
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .note-editor-header {
          position: sticky;
          top: 0;
          z-index: 30;
          background: var(--bs-body-bg, #fff);
          border-bottom: 1px solid rgba(0,0,0,0.06);
          flex: 0 0 auto;
        }

        .note-editor-body {
          flex: 1 1 auto;
          min-height: 0;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        /* ✅ Make the quill root participate in flex height */
        .note-editor-body .ql-toolbar {
          flex: 0 0 auto;
          position: sticky;
          top: 0;
          z-index: 20;
          background: var(--bs-body-bg, #fff);
        }

        .note-editor-body .ql-container {
          flex: 1 1 auto;
         min-height: calc(100vh - 230px);
          display: flex;
          flex-direction: column;
        }

        .note-editor-body .ql-editor {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          overflow-wrap: anywhere;
          word-break: break-word;
        }
      `}</style>
    </div>
  );
};

export default RichNoteEditor;
